import { useEffect, useState } from "react";
import { X, Zap, User } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { ProfileSettings } from "@/services/db";

interface ProfileSettingsPanelProps {
  initial?: ProfileSettings;
  onClose: () => void;
  onSave: (settings: ProfileSettings) => void;
  onActivate?: () => void;
}

export const ProfileSettingsPanel = ({ initial, onClose, onSave, onActivate }: ProfileSettingsPanelProps) => {
  const [draft, setDraft] = useState<ProfileSettings>(
    initial ?? { displayName: "You", username: "chatmcp-user" },
  );

  useEffect(() => {
    setDraft(initial ?? { displayName: "You", username: "chatmcp-user" });
  }, [initial]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/30 px-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl border border-white/60 bg-white/95 p-6 shadow-2xl dark:bg-slate-900">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-wide text-primary">Profile</p>
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-xl font-semibold text-foreground">Profile settings</h3>
            </div>
            <p className="text-sm text-muted-foreground">These details personalize your chats and agent presence.</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close profile settings">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="mt-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Display Name</label>
            <Input
              value={draft.displayName}
              onChange={e => setDraft(prev => ({ ...prev, displayName: e.target.value }))}
              placeholder="Display name"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Username</label>
            <Input
              value={draft.username}
              onChange={e => setDraft(prev => ({ ...prev, username: e.target.value }))}
              placeholder="Username"
            />
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="secondary"
            className="gap-2"
            onClick={() => onActivate?.()}
          >
            <Zap className="h-4 w-4" />
            Activate for advanced features
          </Button>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
